/**
 * ledgerPeriodOf
 *
 * The fiscal period a moment belongs to, in the structure's zone. The index is estimated from the
 * calendar difference to the anchor, then corrected by at most one step either way against the
 * half-open bounds `ledgerPeriodAt` computes, which absorbs month-end clamping and DST. So a moment
 * one minute before local midnight on April 1 is in March. Consumers: posting, period close and
 * aging (SYD-2706 onwards).
 */

import type { DateTime } from 'luxon';
import type { LedgerCalendarStructure } from '../System.Ledger.Definition.types';
import type { LedgerPeriod } from './LedgerPeriod.types';
import { ledgerPeriodAt } from './ledgerPeriodAt';

function ledgerPeriodOf(moment: DateTime, structure: LedgerCalendarStructure): LedgerPeriod {
    const { unit } = structure.fiscalPeriod;
    const diffUnit = unit === 'week' ? 'weeks' : 'months';
    const stepSize = unit === 'quarter' ? 3 : 1;
    const anchor = ledgerPeriodAt(0, structure).startAt;
    const estimate = Math.floor(moment.setZone(structure.timeZone).diff(anchor, diffUnit).as(diffUnit) / stepSize);
    const candidate = ledgerPeriodAt(estimate, structure);
    if (candidate.startAt.toMillis() > moment.toMillis()) return ledgerPeriodAt(estimate - 1, structure);
    if (candidate.endAt.toMillis() <= moment.toMillis()) return ledgerPeriodAt(estimate + 1, structure);
    return candidate;
}

export { ledgerPeriodOf };
